import React, { type CSSProperties } from 'react';
import {
    DefaultPartnerLink,
    getPartnerChipKey,
    getPartnerInitials,
    getPartnerLogoAlt,
    getPartnerLogoSource,
    getPartnerSchemaTypeUrl,
    isPartnerChip,
    makePartnerSoftAccent,
    partnerTokens,
    renderPartnerIcon,
    resolvePartnerLinkProps,
    toPartnerCssSize,
    type PartnerAction,
    type PartnerChip,
    type PartnerColors,
    type PartnerIcon,
    type PartnerIconRenderer,
    type PartnerIconToken,
    type PartnerImageSource,
    type PartnerLinkComponent,
    type PartnerLogo,
    type PartnerSchemaType,
} from '../partner-shared';

export const partnerDirectoryTokens = {
    colors: partnerTokens.colors,
    sizes: {
        maxWidth: partnerTokens.sizes.maxWidth,
        tileGap: partnerTokens.sizes.tileGap,
        minTileWidth: partnerTokens.sizes.directoryTileMinWidth,
    },
} as const;

export type PartnerDirectoryLayout = 'stack' | 'grid';

export type PartnerDirectoryHighlight = {
    id?: string | number;
    title?: React.ReactNode;
    text?: React.ReactNode;
    icon?: PartnerIcon;
    iconComponent?: PartnerIconRenderer;
    iconToken?: PartnerIconToken;
};

export type PartnerDirectoryPartner = {
    id: string | number;
    name: React.ReactNode;
    href: string;
    logo?: PartnerLogo;
    logoSrc?: PartnerImageSource;
    logoAlt?: string;
    description?: React.ReactNode;
    category?: React.ReactNode;
    relationship?: React.ReactNode;
    location?: React.ReactNode;
    whyRecommended?: React.ReactNode;
    howWeWorkTogether?: React.ReactNode;
    highlights?: PartnerDirectoryHighlight[];
    tags?: PartnerChip[];
    profileHref?: string;
    profileLabel?: React.ReactNode;
    linkLabel?: React.ReactNode;
    ariaLabel?: string;
    title?: string;
    target?: string;
    rel?: string | false;
    linkRel?: string | false;
    sponsored?: boolean;
    nofollow?: boolean;
    ugc?: boolean;
    schemaType?: PartnerSchemaType;
    accentColor?: string;
    accentColour?: string;
    accentSoftColor?: string;
    accentSoftColour?: string;
    logoBackgroundColor?: string;
    logoBackgroundColour?: string;
};

export type PartnerDirectoryProps = {
    partners: PartnerDirectoryPartner[];
    LinkComponent?: PartnerLinkComponent;
    Link?: PartnerLinkComponent;
    layout?: PartnerDirectoryLayout;
    eyebrow?: React.ReactNode;
    title?: React.ReactNode;
    titleComponent?: 'h1' | 'h2' | 'h3';
    description?: React.ReactNode;
    action?: PartnerAction | false;
    actionHref?: string;
    actionLabel?: React.ReactNode;
    actionAriaLabel?: string;
    ctaLabel?: React.ReactNode;
    profileCtaLabel?: React.ReactNode;
    whyRecommendedLabel?: React.ReactNode;
    howWeWorkTogetherLabel?: React.ReactNode;
    highlightsLabel?: React.ReactNode;
    tagsLabel?: React.ReactNode;
    disclosure?: React.ReactNode;
    emptyMessage?: React.ReactNode;
    defaultLinkRel?: string | false;
    defaultLinkTarget?: string;
    colors?: PartnerColors;
    colours?: PartnerColors;
    accentColor?: string;
    accentColour?: string;
    backgroundColor?: string;
    backgroundColour?: string;
    surfaceColor?: string;
    surfaceColour?: string;
    textColor?: string;
    textColour?: string;
    maxWidth?: number | string;
    tileGap?: number | string;
    minTileWidth?: number | string;
    className?: string;
    style?: CSSProperties;
    ariaLabel?: string;
};

type PartnerDirectoryStyle = CSSProperties &
    Record<`--partner-directory-${string}`, string | number | undefined>;

type PartnerDirectoryItemStyle = CSSProperties &
    Record<`--partner-directory-${string}`, string | number | undefined>;

const partnerDirectoryCss = `
.partner-directory {
  --partner-directory-bg: ${partnerTokens.colors.background};
  --partner-directory-surface: ${partnerTokens.colors.surface};
  --partner-directory-surface-soft: ${partnerTokens.colors.surfaceSoft};
  --partner-directory-text: ${partnerTokens.colors.text};
  --partner-directory-text-soft: ${partnerTokens.colors.textSoft};
  --partner-directory-text-muted: ${partnerTokens.colors.textMuted};
  --partner-directory-accent: ${partnerTokens.colors.accent};
  --partner-directory-accent-soft: ${partnerTokens.colors.accentSoft};
  --partner-directory-action: ${partnerTokens.colors.action};
  --partner-directory-action-soft: ${partnerTokens.colors.actionSoft};
  --partner-directory-border: ${partnerTokens.colors.border};
  --partner-directory-border-strong: ${partnerTokens.colors.borderStrong};
  --partner-directory-chip-bg: ${partnerTokens.colors.chipBackground};
  --partner-directory-logo-bg: ${partnerTokens.colors.logoBackground};
  --partner-directory-shadow: ${partnerTokens.colors.shadow};
  --partner-directory-hover-shadow: ${partnerTokens.colors.hoverShadow};
  --partner-directory-max-width: ${partnerDirectoryTokens.sizes.maxWidth}px;
  --partner-directory-tile-gap: ${partnerDirectoryTokens.sizes.tileGap}px;
  --partner-directory-min-tile-width: ${partnerDirectoryTokens.sizes.minTileWidth}px;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  background: transparent;
  color: var(--partner-directory-text);
  font-family: ${partnerTokens.typography.fontFamily};
  box-sizing: border-box;
  container-type: inline-size;
}
.partner-directory,
.partner-directory * {
  box-sizing: border-box;
}
.partner-directory-shell {
  width: 100%;
  max-width: var(--partner-directory-max-width);
  margin: 0 auto;
  padding: clamp(26px, 3vw, 42px);
  border: 1px solid var(--partner-directory-border-strong);
  border-radius: 14px;
  background: var(--partner-directory-bg);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.82);
}
.partner-directory-header-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 24px;
  align-items: center;
  margin: 0 0 26px;
}
.partner-directory-header {
  display: grid;
  max-width: 800px;
  gap: 10px;
}
.partner-directory-eyebrow {
  display: inline-flex;
  width: fit-content;
  max-width: 100%;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: var(--partner-directory-accent);
  font-size: 11px;
  line-height: 1;
  font-weight: 900;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  overflow-wrap: anywhere;
}
.partner-directory-eyebrow::after {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: currentColor;
}
.partner-directory-title {
  margin: 0;
  color: var(--partner-directory-text);
  font-size: clamp(34px, 4vw, 54px);
  line-height: 1.08;
  font-weight: 850;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.partner-directory-title-highlight {
  color: var(--partner-directory-accent);
}
.partner-directory-description {
  max-width: 720px;
  margin: 0;
  color: var(--partner-directory-text-soft);
  font-size: 15px;
  line-height: 1.58;
  font-weight: 500;
  letter-spacing: 0;
}
.partner-directory-action {
  display: inline-flex;
  min-height: 52px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 1px solid color-mix(in srgb, var(--partner-directory-action) 42%, var(--partner-directory-border));
  border-radius: 8px;
  background: color-mix(in srgb, var(--partner-directory-surface) 92%, var(--partner-directory-action) 8%);
  color: var(--partner-directory-action);
  padding: 0 18px;
  text-decoration: none;
  font-size: 14px;
  line-height: 1;
  font-weight: 850;
  letter-spacing: 0;
  box-shadow: 0 10px 22px rgba(17, 24, 39, 0.04);
  transition: transform 170ms ease, border-color 170ms ease, box-shadow 170ms ease;
}
.partner-directory-action:hover {
  transform: translateY(-1px);
  border-color: var(--partner-directory-action);
  box-shadow: 0 14px 30px rgba(17, 24, 39, 0.08);
}
.partner-directory-action:focus-visible,
.partner-directory-link:focus-visible,
.partner-directory-profile-link:focus-visible,
.partner-directory-chip-link:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--partner-directory-action) 28%, transparent);
  outline-offset: 3px;
}
.partner-directory-action-text,
.partner-directory-link-text,
.partner-directory-profile-link span {
  min-width: 0;
  overflow-wrap: anywhere;
}
.partner-directory-list {
  display: grid;
  min-width: 0;
  gap: var(--partner-directory-tile-gap);
}
.partner-directory-list--grid {
  grid-template-columns: repeat(auto-fit, minmax(min(100%, var(--partner-directory-min-tile-width)), 1fr));
}
.partner-directory-list--stack {
  grid-template-columns: 1fr;
}
.partner-directory-card {
  --partner-directory-card-accent: var(--partner-directory-accent);
  --partner-directory-card-accent-soft: var(--partner-directory-accent-soft);
  display: grid;
  position: relative;
  min-width: 0;
  overflow: hidden;
  gap: 22px;
  border: 1px solid var(--partner-directory-border);
  border-radius: 8px;
  background: var(--partner-directory-surface);
  color: var(--partner-directory-text);
  padding: 20px;
  box-shadow: var(--partner-directory-shadow);
}
.partner-directory-card::before {
  content: "";
  position: absolute;
  inset: 0 0 auto;
  height: 4px;
  background: var(--partner-directory-card-accent);
}
.partner-directory-list--stack .partner-directory-card {
  grid-template-columns: minmax(220px, 0.62fr) minmax(0, 1fr);
  align-items: stretch;
}
.partner-directory-card-summary {
  display: grid;
  min-width: 0;
  align-content: start;
  gap: 16px;
}
.partner-directory-card-main {
  display: grid;
  min-width: 0;
  gap: 18px;
}
.partner-directory-logo-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 14px;
  align-items: center;
  min-width: 0;
}
.partner-directory-logo {
  display: flex;
  width: 72px;
  height: 72px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--partner-directory-card-accent) 12%, var(--partner-directory-border));
  border-radius: 8px;
  background: var(--partner-directory-card-logo-bg, var(--partner-directory-logo-bg));
  color: var(--partner-directory-card-accent);
  font-size: 21px;
  line-height: 1;
  font-weight: 900;
  letter-spacing: 0;
}
.partner-directory-logo-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  padding: 10px;
}
.partner-directory-card-title {
  margin: 0;
  color: var(--partner-directory-text);
  font-size: 22px;
  line-height: 1.14;
  font-weight: 850;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.partner-directory-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 8px;
  margin: 8px 0 0;
  color: var(--partner-directory-text-muted);
  font-size: 12px;
  line-height: 1.3;
  font-weight: 700;
  letter-spacing: 0;
}
.partner-directory-meta span + span::before {
  content: "/";
  margin-right: 8px;
  color: color-mix(in srgb, var(--partner-directory-text-muted) 58%, transparent);
}
.partner-directory-relationship {
  display: inline-flex;
  width: fit-content;
  max-width: 100%;
  align-items: center;
  gap: 7px;
  border-radius: 999px;
  background: var(--partner-directory-card-accent-soft);
  color: var(--partner-directory-card-accent);
  padding: 8px 11px;
  font-size: 12px;
  line-height: 1;
  font-weight: 850;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.partner-directory-description-text {
  margin: 0;
  color: var(--partner-directory-text-soft);
  font-size: 15px;
  line-height: 1.58;
  font-weight: 500;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.partner-directory-section-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.partner-directory-info-block {
  display: grid;
  min-width: 0;
  align-content: start;
  gap: 8px;
  border: 1px solid var(--partner-directory-border);
  border-radius: 8px;
  background: color-mix(in srgb, var(--partner-directory-surface) 80%, var(--partner-directory-card-accent-soft));
  padding: 14px;
}
.partner-directory-info-title,
.partner-directory-highlights-title,
.partner-directory-tags-title {
  margin: 0;
  color: var(--partner-directory-text);
  font-size: 12px;
  line-height: 1.2;
  font-weight: 900;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
.partner-directory-info-text {
  margin: 0;
  color: var(--partner-directory-text-soft);
  font-size: 14px;
  line-height: 1.5;
  font-weight: 500;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.partner-directory-highlights {
  display: grid;
  gap: 10px;
}
.partner-directory-highlight-list {
  display: grid;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.partner-directory-highlight {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 10px;
  align-items: start;
}
.partner-directory-highlight-icon {
  display: inline-flex;
  width: 30px;
  height: 30px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: var(--partner-directory-card-accent-soft);
  color: var(--partner-directory-card-accent);
}
.partner-directory-highlight-copy {
  min-width: 0;
}
.partner-directory-highlight-title {
  margin: 0;
  color: var(--partner-directory-text);
  font-size: 14px;
  line-height: 1.25;
  font-weight: 820;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.partner-directory-highlight-text {
  margin: 3px 0 0;
  color: var(--partner-directory-text-soft);
  font-size: 13px;
  line-height: 1.45;
  font-weight: 500;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.partner-directory-chip-group {
  display: grid;
  gap: 9px;
}
.partner-directory-chip-list {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  gap: 8px;
}
.partner-directory-chip,
.partner-directory-chip-link {
  display: inline-flex;
  max-width: 100%;
  align-items: center;
  border: 0;
  border-radius: 999px;
  background: var(--partner-directory-chip-surface, var(--partner-directory-chip-bg));
  color: var(--partner-directory-chip-text, var(--partner-directory-text-soft));
  padding: 7px 10px;
  text-decoration: none;
  font-size: 11px;
  line-height: 1;
  font-weight: 750;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.partner-directory-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-top: auto;
}
.partner-directory-link,
.partner-directory-profile-link {
  display: inline-flex;
  max-width: 100%;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 8px;
  text-decoration: none;
  font-size: 13px;
  line-height: 1;
  font-weight: 850;
  letter-spacing: 0;
  padding: 0 14px;
}
.partner-directory-link {
  border: 1px solid var(--partner-directory-card-accent);
  background: var(--partner-directory-card-accent);
  color: #ffffff;
}
.partner-directory-profile-link {
  border: 1px solid color-mix(in srgb, var(--partner-directory-card-accent) 30%, var(--partner-directory-border));
  background: var(--partner-directory-surface);
  color: var(--partner-directory-card-accent);
}
.partner-directory-link .partner-directory-icon,
.partner-directory-profile-link .partner-directory-icon,
.partner-directory-action .partner-directory-icon {
  transition: transform 180ms ease;
}
.partner-directory-link:hover .partner-directory-icon,
.partner-directory-profile-link:hover .partner-directory-icon,
.partner-directory-action:hover .partner-directory-icon {
  transform: translateX(3px);
}
.partner-directory-icon,
.partner-directory-icon svg {
  display: block;
  width: var(--partner-directory-icon-size, 16px);
  height: var(--partner-directory-icon-size, 16px);
  flex: 0 0 auto;
}
.partner-directory-empty {
  display: flex;
  min-height: 150px;
  align-items: center;
  justify-content: center;
  border: 1px dashed var(--partner-directory-border);
  border-radius: 8px;
  background: color-mix(in srgb, var(--partner-directory-surface) 76%, var(--partner-directory-accent-soft));
  color: var(--partner-directory-text-muted);
  padding: 24px;
  text-align: center;
  font-size: 15px;
  line-height: 1.45;
  font-weight: 750;
}
.partner-directory-disclosure {
  max-width: 820px;
  margin: 18px 0 0;
  color: var(--partner-directory-text-muted);
  font-size: 12px;
  line-height: 1.55;
  font-weight: 550;
  letter-spacing: 0;
}
@supports not (color: color-mix(in srgb, red 50%, white)) {
  .partner-directory-link {
    background: var(--partner-directory-card-accent);
  }
}
@container (max-width: 900px) {
  .partner-directory-list--stack .partner-directory-card {
    grid-template-columns: 1fr;
  }
  .partner-directory-section-grid {
    grid-template-columns: 1fr;
  }
}
@container (max-width: 760px) {
  .partner-directory-shell {
    padding: 22px;
  }
  .partner-directory-header-row {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  .partner-directory-title {
    font-size: 34px;
  }
  .partner-directory-action {
    width: fit-content;
    min-height: 48px;
  }
}
@container (max-width: 460px) {
  .partner-directory-shell {
    padding: 18px;
  }
  .partner-directory-card {
    padding: 16px;
  }
  .partner-directory-logo-row {
    grid-template-columns: 1fr;
  }
  .partner-directory-logo {
    width: 64px;
    height: 64px;
  }
}
`;

function shouldRender(value: React.ReactNode) {
    return value !== undefined && value !== null && value !== false && value !== '';
}

function PartnerDirectoryLogo({ partner }: { partner: PartnerDirectoryPartner }) {
    const logoSrc = getPartnerLogoSource(partner.logo, partner.logoSrc);
    const dimensions =
        partner.logo?.src && typeof partner.logo.src === 'object'
            ? {
                  width: partner.logo.width || partner.logo.src.width,
                  height: partner.logo.height || partner.logo.src.height,
              }
            : { width: partner.logo?.width, height: partner.logo?.height };

    return (
        <span className="partner-directory-logo" aria-hidden={logoSrc ? undefined : true}>
            {logoSrc ? (
                <img
                    className="partner-directory-logo-image"
                    src={logoSrc}
                    alt={getPartnerLogoAlt({
                        logo: partner.logo,
                        logoAlt: partner.logoAlt,
                        name: partner.name,
                    })}
                    width={dimensions.width}
                    height={dimensions.height}
                    loading={partner.logo?.loading || 'lazy'}
                    decoding={partner.logo?.decoding || 'async'}
                    itemProp="logo"
                    style={{
                        objectFit: partner.logo?.objectFit,
                        objectPosition: partner.logo?.objectPosition,
                    }}
                />
            ) : (
                <span>{getPartnerInitials(partner.name)}</span>
            )}
        </span>
    );
}

function renderDirectoryAction({
    action,
    actionHref,
    actionLabel,
    actionAriaLabel,
    ComponentLink,
    defaultLinkRel,
    defaultLinkTarget,
}: {
    action?: PartnerAction | false;
    actionHref?: string;
    actionLabel?: React.ReactNode;
    actionAriaLabel?: string;
    ComponentLink: PartnerLinkComponent;
    defaultLinkRel?: string | false;
    defaultLinkTarget?: string;
}) {
    if (action === false) return null;

    const resolvedAction =
        action ||
        (actionHref && actionLabel
            ? {
                  href: actionHref,
                  label: actionLabel,
                  ariaLabel: actionAriaLabel,
                  iconToken: 'arrowRight' as PartnerIconToken,
              }
            : null);

    if (!resolvedAction) return null;

    const linkProps = resolvePartnerLinkProps({
        rel: resolvedAction.rel,
        linkRel: resolvedAction.linkRel,
        target: resolvedAction.target,
        sponsored: resolvedAction.sponsored,
        nofollow: resolvedAction.nofollow,
        ugc: resolvedAction.ugc,
        defaultRel: defaultLinkRel,
        defaultTarget: defaultLinkTarget,
    });

    return (
        <ComponentLink
            href={resolvedAction.href}
            className="partner-directory-action"
            aria-label={resolvedAction.ariaLabel}
            title={resolvedAction.title}
            {...linkProps}
        >
            {renderPartnerIcon({
                icon: resolvedAction.icon,
                iconComponent: resolvedAction.iconComponent,
                iconToken: resolvedAction.iconToken || 'arrowRight',
                size: 18,
                className: 'partner-directory-icon',
                cssVariable: '--partner-directory-icon-size',
            })}
            <span className="partner-directory-action-text">{resolvedAction.label}</span>
            {renderPartnerIcon({
                iconToken: 'arrowRight',
                size: 16,
                className: 'partner-directory-icon',
                cssVariable: '--partner-directory-icon-size',
            })}
        </ComponentLink>
    );
}

export default function PartnerDirectory({
    partners,
    LinkComponent,
    Link,
    layout = 'stack',
    eyebrow,
    title,
    titleComponent = 'h2',
    description,
    action,
    actionHref,
    actionLabel,
    actionAriaLabel,
    ctaLabel = 'Visit partner website',
    profileCtaLabel = 'View partner profile',
    whyRecommendedLabel = 'Why we recommend them',
    howWeWorkTogetherLabel = 'How we work together',
    highlightsLabel = 'What stands out',
    tagsLabel,
    disclosure,
    emptyMessage = 'No partners available.',
    defaultLinkRel,
    defaultLinkTarget,
    colors,
    colours,
    accentColor,
    accentColour,
    backgroundColor,
    backgroundColour,
    surfaceColor,
    surfaceColour,
    textColor,
    textColour,
    maxWidth,
    tileGap,
    minTileWidth,
    className,
    style,
    ariaLabel,
}: PartnerDirectoryProps) {
    const ComponentLink = LinkComponent || Link || DefaultPartnerLink;
    const mergedColors = { ...colors, ...colours };
    const resolvedAccent =
        accentColor || accentColour || mergedColors.accent || partnerTokens.colors.accent;
    const resolvedAccentSoft = mergedColors.accentSoft || makePartnerSoftAccent(resolvedAccent);
    const hasHeader = !!(eyebrow || title || description);
    const TitleTag = titleComponent;
    const sectionStyle: PartnerDirectoryStyle = {
        ...style,
        '--partner-directory-bg': backgroundColor || backgroundColour || mergedColors.background,
        '--partner-directory-surface': surfaceColor || surfaceColour || mergedColors.surface,
        '--partner-directory-surface-soft': mergedColors.surfaceSoft,
        '--partner-directory-text': textColor || textColour || mergedColors.text,
        '--partner-directory-text-soft': mergedColors.textSoft,
        '--partner-directory-text-muted': mergedColors.textMuted,
        '--partner-directory-accent': resolvedAccent,
        '--partner-directory-accent-soft': resolvedAccentSoft,
        '--partner-directory-action': mergedColors.action,
        '--partner-directory-action-soft': mergedColors.actionSoft,
        '--partner-directory-border': mergedColors.border,
        '--partner-directory-border-strong': mergedColors.borderStrong,
        '--partner-directory-chip-bg': mergedColors.chipBackground,
        '--partner-directory-logo-bg': mergedColors.logoBackground,
        '--partner-directory-shadow': mergedColors.shadow,
        '--partner-directory-hover-shadow': mergedColors.hoverShadow,
        '--partner-directory-max-width': toPartnerCssSize(maxWidth),
        '--partner-directory-tile-gap': toPartnerCssSize(tileGap),
        '--partner-directory-min-tile-width': toPartnerCssSize(minTileWidth),
    };
    const renderedAction = renderDirectoryAction({
        action,
        actionHref,
        actionLabel,
        actionAriaLabel,
        ComponentLink,
        defaultLinkRel,
        defaultLinkTarget,
    });

    return (
        <section
            className={['partner-directory', className].filter(Boolean).join(' ')}
            style={sectionStyle}
            aria-label={ariaLabel}
            data-component="partner-directory"
        >
            <style>{partnerDirectoryCss}</style>

            <div className="partner-directory-shell">
                {hasHeader || renderedAction ? (
                    <div className="partner-directory-header-row">
                        {hasHeader ? (
                            <header className="partner-directory-header">
                                {eyebrow ? (
                                    <p className="partner-directory-eyebrow">{eyebrow}</p>
                                ) : null}
                                {title ? (
                                    <TitleTag className="partner-directory-title">
                                        {title}
                                    </TitleTag>
                                ) : null}
                                {description ? (
                                    <p className="partner-directory-description">{description}</p>
                                ) : null}
                            </header>
                        ) : (
                            <span aria-hidden="true" />
                        )}
                        {renderedAction}
                    </div>
                ) : null}

                {partners.length ? (
                    <div
                        className={[
                            'partner-directory-list',
                            `partner-directory-list--${layout}`,
                        ].join(' ')}
                    >
                        {partners.map((partner) => {
                            const partnerAccent =
                                partner.accentColor || partner.accentColour || resolvedAccent;
                            const partnerAccentSoft =
                                partner.accentSoftColor ||
                                partner.accentSoftColour ||
                                makePartnerSoftAccent(partnerAccent) ||
                                resolvedAccentSoft;
                            const partnerLinkProps = resolvePartnerLinkProps({
                                rel: partner.rel,
                                linkRel: partner.linkRel,
                                target: partner.target,
                                sponsored: partner.sponsored,
                                nofollow: partner.nofollow,
                                ugc: partner.ugc,
                                defaultRel: defaultLinkRel,
                                defaultTarget: defaultLinkTarget,
                            });
                            const itemStyle: PartnerDirectoryItemStyle = {
                                '--partner-directory-card-accent': partnerAccent,
                                '--partner-directory-card-accent-soft': partnerAccentSoft,
                                '--partner-directory-card-logo-bg':
                                    partner.logoBackgroundColor ||
                                    partner.logoBackgroundColour ||
                                    partner.logo?.backgroundColor ||
                                    partner.logo?.backgroundColour,
                            };
                            const hasInfoBlocks =
                                shouldRender(partner.whyRecommended) ||
                                shouldRender(partner.howWeWorkTogether);
                            const hasHighlights = !!partner.highlights?.length;
                            const hasTags = !!partner.tags?.length;

                            return (
                                <article
                                    className="partner-directory-card"
                                    style={itemStyle}
                                    key={partner.id}
                                    itemScope
                                    itemType={getPartnerSchemaTypeUrl(partner.schemaType)}
                                >
                                    <div className="partner-directory-card-summary">
                                        <div className="partner-directory-logo-row">
                                            <PartnerDirectoryLogo partner={partner} />
                                            <div>
                                                <h3
                                                    className="partner-directory-card-title"
                                                    itemProp="name"
                                                >
                                                    {partner.name}
                                                </h3>
                                                {partner.category || partner.location ? (
                                                    <p className="partner-directory-meta">
                                                        {shouldRender(partner.category) ? (
                                                            <span>{partner.category}</span>
                                                        ) : null}
                                                        {shouldRender(partner.location) ? (
                                                            <span>{partner.location}</span>
                                                        ) : null}
                                                    </p>
                                                ) : null}
                                            </div>
                                        </div>

                                        {partner.relationship ? (
                                            <span className="partner-directory-relationship">
                                                {renderPartnerIcon({
                                                    iconToken: 'handshake',
                                                    size: 14,
                                                    className: 'partner-directory-icon',
                                                    cssVariable: '--partner-directory-icon-size',
                                                })}
                                                <span>{partner.relationship}</span>
                                            </span>
                                        ) : null}

                                        {partner.description ? (
                                            <p
                                                className="partner-directory-description-text"
                                                itemProp="description"
                                            >
                                                {partner.description}
                                            </p>
                                        ) : null}

                                        <div className="partner-directory-actions">
                                            <ComponentLink
                                                href={partner.href}
                                                className="partner-directory-link"
                                                aria-label={partner.ariaLabel}
                                                title={partner.title}
                                                itemProp="url"
                                                {...partnerLinkProps}
                                            >
                                                <span className="partner-directory-link-text">
                                                    {partner.linkLabel || ctaLabel}
                                                </span>
                                                {renderPartnerIcon({
                                                    iconToken: 'externalLink',
                                                    size: 15,
                                                    className: 'partner-directory-icon',
                                                    cssVariable: '--partner-directory-icon-size',
                                                })}
                                            </ComponentLink>

                                            {partner.profileHref ? (
                                                <ComponentLink
                                                    href={partner.profileHref}
                                                    className="partner-directory-profile-link"
                                                >
                                                    <span>
                                                        {partner.profileLabel || profileCtaLabel}
                                                    </span>
                                                    {renderPartnerIcon({
                                                        iconToken: 'arrowRight',
                                                        size: 15,
                                                        className: 'partner-directory-icon',
                                                        cssVariable:
                                                            '--partner-directory-icon-size',
                                                    })}
                                                </ComponentLink>
                                            ) : null}
                                        </div>
                                    </div>

                                    <div className="partner-directory-card-main">
                                        {hasInfoBlocks ? (
                                            <div className="partner-directory-section-grid">
                                                {partner.whyRecommended ? (
                                                    <section className="partner-directory-info-block">
                                                        <h4 className="partner-directory-info-title">
                                                            {whyRecommendedLabel}
                                                        </h4>
                                                        <p className="partner-directory-info-text">
                                                            {partner.whyRecommended}
                                                        </p>
                                                    </section>
                                                ) : null}
                                                {partner.howWeWorkTogether ? (
                                                    <section className="partner-directory-info-block">
                                                        <h4 className="partner-directory-info-title">
                                                            {howWeWorkTogetherLabel}
                                                        </h4>
                                                        <p className="partner-directory-info-text">
                                                            {partner.howWeWorkTogether}
                                                        </p>
                                                    </section>
                                                ) : null}
                                            </div>
                                        ) : null}

                                        {hasHighlights ? (
                                            <section className="partner-directory-highlights">
                                                <h4 className="partner-directory-highlights-title">
                                                    {highlightsLabel}
                                                </h4>
                                                <ul className="partner-directory-highlight-list">
                                                    {partner.highlights?.map((highlight, index) => (
                                                        <li
                                                            className="partner-directory-highlight"
                                                            key={
                                                                highlight.id ||
                                                                `${partner.id}-highlight-${index}`
                                                            }
                                                        >
                                                            <span className="partner-directory-highlight-icon">
                                                                {renderPartnerIcon({
                                                                    icon: highlight.icon,
                                                                    iconComponent:
                                                                        highlight.iconComponent,
                                                                    iconToken:
                                                                        highlight.iconToken ||
                                                                        'checkCircle',
                                                                    size: 15,
                                                                    className:
                                                                        'partner-directory-icon',
                                                                    cssVariable:
                                                                        '--partner-directory-icon-size',
                                                                })}
                                                            </span>
                                                            <span className="partner-directory-highlight-copy">
                                                                {highlight.title ? (
                                                                    <span className="partner-directory-highlight-title">
                                                                        {highlight.title}
                                                                    </span>
                                                                ) : null}
                                                                {highlight.text ? (
                                                                    <span className="partner-directory-highlight-text">
                                                                        {highlight.text}
                                                                    </span>
                                                                ) : null}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </section>
                                        ) : null}

                                        {hasTags ? (
                                            <section className="partner-directory-chip-group">
                                                {tagsLabel ? (
                                                    <h4 className="partner-directory-tags-title">
                                                        {tagsLabel}
                                                    </h4>
                                                ) : null}
                                                <div className="partner-directory-chip-list">
                                                    {partner.tags?.map((tag, index) => {
                                                        const chip = isPartnerChip(tag)
                                                            ? tag
                                                            : { label: tag };
                                                        const chipStyle = {
                                                            '--partner-directory-chip-surface':
                                                                chip.backgroundColor ||
                                                                chip.backgroundColour,
                                                            '--partner-directory-chip-text':
                                                                chip.textColor ||
                                                                chip.textColour ||
                                                                chip.color ||
                                                                chip.colour,
                                                        } as CSSProperties;
                                                        if (chip.href) {
                                                            return (
                                                                <ComponentLink
                                                                    href={chip.href}
                                                                    className="partner-directory-chip-link"
                                                                    style={chipStyle}
                                                                    target={chip.target}
                                                                    rel={chip.rel}
                                                                    title={chip.title}
                                                                    aria-label={chip.ariaLabel}
                                                                    key={getPartnerChipKey(
                                                                        tag,
                                                                        index,
                                                                    )}
                                                                >
                                                                    {chip.label}
                                                                </ComponentLink>
                                                            );
                                                        }

                                                        return (
                                                            <span
                                                                className="partner-directory-chip"
                                                                style={chipStyle}
                                                                aria-label={chip.ariaLabel}
                                                                key={getPartnerChipKey(tag, index)}
                                                            >
                                                                {chip.label}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            </section>
                                        ) : null}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                ) : (
                    <div className="partner-directory-empty">{emptyMessage}</div>
                )}

                {disclosure ? <p className="partner-directory-disclosure">{disclosure}</p> : null}
            </div>
        </section>
    );
}
